
import { Clear as Eraser, Mouse as MousePointer2, Brush as Paintbrush, TouchApp as SelectorIcon } from "@mui/icons-material";
import { Box, Button, Divider, FormControl, MenuItem, Select, Slider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useEffect } from 'react';
import { useTranslation } from "react-i18next";

import { MAP_SYMBOLS, SYMBOL_CATEGORIES, getSymbolsByCategory } from "@/lib/map-assets";
import type { PlacedSymbol, SymbolMode } from "../canvas/types";
import { sliderValue } from '../canvas/slider-utils';

interface SymbolPanelProps {
    activeSymbol: string;
    setActiveSymbol: (symbol: string) => void;
    activeSymbolCategory: string;
    setActiveSymbolCategory: (category: string) => void;
    brushSize: number;
    setBrushSize: (size: number) => void;
    paintOpacity?: number;
    setPaintOpacity?: (value: number) => void;
    symbolMode?: SymbolMode;
    setSymbolMode?: (mode: SymbolMode) => void;
    stampDensity?: number;
    setStampDensity?: (density: number) => void;
    symbolSize?: number;
    setSymbolSize?: (size: number) => void;
    symbolStyle?: 'colored' | 'empty';
    setSymbolStyle?: (style: 'colored' | 'empty') => void;
    activeSymbols?: string[];
    setActiveSymbols?: (symbols: string[]) => void;
    processedSheets: Record<string, HTMLCanvasElement | HTMLImageElement>;
    placedSymbols?: PlacedSymbol[];
}

export function SymbolPanel({
    activeSymbol,
    setActiveSymbol,
    activeSymbolCategory,
    setActiveSymbolCategory,
    brushSize,
    setBrushSize,
    symbolMode = 'single',
    setSymbolMode,
    stampDensity = 0.3,
    setStampDensity,
    symbolSize = 20,
    setSymbolSize,
    activeSymbols = [],
    setActiveSymbols,
    placedSymbols = [],
}: SymbolPanelProps) {
    const { t } = useTranslation();
    const filteredSymbols = activeSymbolCategory === 'all'
        ? MAP_SYMBOLS
        : getSymbolsByCategory(activeSymbolCategory as Parameters<typeof getSymbolsByCategory>[0]);

    const totalPlaced = placedSymbols.length;
    const maxCapacity = 2000;
    const isNearingLimit = totalPlaced >= maxCapacity * 0.5;
    const consumptionPercent = Math.round((totalPlaced / maxCapacity) * 100);

    useEffect(() => {
        if (filteredSymbols.length > 0) {
            const firstId = filteredSymbols[0].id;
            setActiveSymbol(firstId);
            if (setActiveSymbols) setActiveSymbols([firstId]);
        }
    }, [activeSymbolCategory]);

    return (
        <Stack spacing={2} sx={{ pb: 2 }}>
            {/* Mode Toggle: Single vs Brush */}
            {setSymbolMode && (
                <>
                    <Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block' }}>
                            {t('map_creator.symbol_panel.mode')}
                        </Typography>
                        <ToggleButtonGroup
                            value={symbolMode}
                            exclusive
                            onChange={(_, mode) => mode && setSymbolMode(mode)}
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 0.5,
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                }
                            }}
                        >
                            <ToggleButton value="select" aria-label="select and move symbols">
                                <Tooltip title={t('map_creator.symbol_panel.select_tooltip')}>
                                    <Stack direction="row" alignItems="center">
                                        <SelectorIcon sx={{ fontSize: '14px' }} />
                                        <Box ml={0.5}>{t('map_creator.symbol_panel.select')}</Box>
                                    </Stack>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="single" aria-label="single placement">
                                <Tooltip title={t('map_creator.symbol_panel.single_tooltip')}>
                                    <Stack direction="row" alignItems="center">
                                        <MousePointer2 sx={{ fontSize: '14px' }} />
                                        <Box ml={0.5}>{t('map_creator.symbol_panel.single')}</Box>
                                    </Stack>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="brush" aria-label="brush placement">
                                <Tooltip title={t('map_creator.symbol_panel.brush_tooltip')}>
                                    <Stack direction="row" alignItems="center">
                                        <Paintbrush sx={{ fontSize: '14px' }} />
                                        <Box ml={0.5}>{t('map_creator.symbol_panel.brush')}</Box>
                                    </Stack>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="eraser" aria-label="erase symbols">
                                <Tooltip title={t('map_creator.symbol_panel.eraser_tooltip')}>
                                    <Stack direction="row" alignItems="center">
                                        <Eraser sx={{ fontSize: '14px' }} />
                                        <Box ml={0.5}>{t('map_creator.symbol_panel.eraser')}</Box>
                                    </Stack>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {isNearingLimit && (
                            <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.contrastText', border: '1px solid', borderColor: 'warning.main', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                    ⚠️ {t('map_creator.symbol_panel.capacity_warning_title')}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                    {t('map_creator.symbol_panel.capacity_warning_desc', {
                                        percent: consumptionPercent,
                                        count: totalPlaced,
                                        max: maxCapacity,
                                    })}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Divider />
                </>
            )}

            {/* Category Filter */}
            {symbolMode !== 'eraser' && symbolMode !== 'select' && (
                <>
                    <Stack spacing={1}>
                        <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>{t('map_creator.symbol_panel.filter')}</Typography>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={activeSymbolCategory}
                                onChange={(e) => setActiveSymbolCategory(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="all">{t('map_creator.symbol_panel.all_symbols')}</MenuItem>
                                {SYMBOL_CATEGORIES.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{t(`map_creator.symbol_panel.categories.${c.id}`)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Divider />
                </>
            )}

            {/* Scrollable Grid */}
            {symbolMode !== 'eraser' && symbolMode !== 'select' && (
                <>
                    <Box sx={{ height: 260, overflowY: 'auto', pr: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1} pb={1}>
                            {filteredSymbols.map(s => {
                                const isSelected = symbolMode === 'brush'
                                    ? activeSymbols.includes(s.id)
                                    : activeSymbol === s.id;

                                const handleToggle = () => {
                                    if (symbolMode === 'brush' && setActiveSymbols) {
                                        if (activeSymbols.includes(s.id)) {
                                            // Don't deselect the last one
                                            if (activeSymbols.length > 1) {
                                                setActiveSymbols(activeSymbols.filter(id => id !== s.id));
                                            }
                                        } else {
                                            setActiveSymbols([...activeSymbols, s.id]);
                                        }
                                        // Also update activeSymbol as the "lead" one
                                        setActiveSymbol(s.id);
                                    } else {
                                        setActiveSymbol(s.id);
                                        if (setActiveSymbols) setActiveSymbols([s.id]);
                                    }
                                };

                                return (
                                    <Button
                                        key={s.id}
                                        variant={isSelected ? 'contained' : 'outlined'}
                                        sx={{
                                            height: 'auto',
                                            aspectRatio: '1/1',
                                            p: 0.5,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            bgcolor: isSelected ? 'primary.main' : 'background.paper',
                                            color: isSelected ? 'primary.contrastText' : 'text.primary',
                                            '&:hover': {
                                                bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                            },
                                            position: 'relative'
                                        }}
                                        onClick={handleToggle}
                                        title={`${s.label} (${s.subcategory})`}
                                    >
                                        {symbolMode === 'brush' && activeSymbols.includes(s.id) && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    width: 12,
                                                    height: 12,
                                                    bgcolor: 'secondary.main',
                                                    borderRadius: '50%',
                                                    border: '2px solid white',
                                                    zIndex: 2
                                                }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                width: '100%',
                                                flex: 1,
                                                bgcolor: 'transparent', // Changed from grey.100
                                                borderRadius: 0.5,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative'
                                            }}
                                        >
                                            {s.src && (
                                                <Box
                                                    component="img"
                                                    src={s.src}
                                                    sx={{
                                                        maxWidth: '90%',
                                                        maxHeight: '90%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" noWrap sx={{ width: '100%', fontSize: '0.6rem', textAlign: 'center', lineHeight: 1 }}>
                                            {s.label}
                                        </Typography>
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}            {/* Controls based on mode */}
            <Stack spacing={3} pt={1}>
                {symbolMode === 'select' && (
                    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                            {t('map_creator.symbol_panel.select_instructions_title')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.4 }}>
                            • {t('map_creator.symbol_panel.select_instructions_drag')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.4, mt: 0.5 }}>
                            • {t('map_creator.symbol_panel.select_instructions_erase')}
                        </Typography>
                    </Box>
                )}

                {(symbolMode === 'brush' || symbolMode === 'eraser') && (
                    <Box>
                        {/* Brush/Eraser Mode: Brush Size */}
                        <Box>
                            <Typography variant="caption" gutterBottom>
                                {symbolMode === 'eraser' ? t('map_creator.symbol_panel.eraser_size', { size: brushSize }) : t('map_creator.symbol_panel.brush_size', { size: brushSize })}
                            </Typography>
                            <Slider
                                min={1}
                                max={10}
                                step={1}
                                value={brushSize}
                                onChange={(_, v) => setBrushSize(sliderValue(v))}
                                size="small"
                            />
                            <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                {symbolMode === 'eraser' ? t('map_creator.symbol_panel.clear_symbols_desc') : t('map_creator.symbol_panel.scatter_symbols_desc')}
                            </Typography>
                        </Box>

                        {symbolMode === 'brush' && (
                            <Box sx={{ mt: 3 }}>
                                {setSymbolSize && (
                                    <Box mb={3}>
                                        <Typography variant="caption" gutterBottom>{t('map_creator.symbol_panel.symbol_size', { size: Math.round(symbolSize / 5) })}</Typography>
                                        <Slider
                                            min={1}
                                            max={20}
                                            step={1}
                                            value={Math.round(symbolSize / 5)}
                                            onChange={(_, v) => setSymbolSize(sliderValue(v) * 5)}
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                            {t('map_creator.symbol_panel.placed_symbol_desc')}
                                        </Typography>
                                    </Box>
                                )}

                                {setStampDensity && (
                                    <Box>
                                        <Typography variant="caption" gutterBottom>
                                            {t('map_creator.symbol_panel.density', { percent: Math.round(stampDensity * 100) })}
                                        </Typography>
                                        <Slider
                                            min={0.1}
                                            max={1.0}
                                            step={0.1}
                                            value={stampDensity}
                                            onChange={(_, v) => setStampDensity(sliderValue(v))}
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                            {t('map_creator.symbol_panel.density_desc')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {symbolMode === 'single' && (
                    <Box>
                        {/* Single Mode: Just Symbol Size */}
                        <Box>
                            <Typography variant="caption" gutterBottom>{t('map_creator.symbol_panel.symbol_size', { size: Math.round(symbolSize / 5) })}</Typography>
                            <Slider
                                min={1}
                                max={10}
                                step={1}
                                value={Math.round(symbolSize / 5)}
                                onChange={(_, v) => setSymbolSize?.(sliderValue(v) * 5)}
                                size="small"
                            />
                        </Box>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}
